"use client";

import type { AnalyticsEnvironment, ExperimentContext } from "@health/types";
import { createContext, type ReactNode, useContext, useMemo } from "react";

/**
 * Shared analytics context read by `useTrack` and its widget consumers.
 * Populated once by the host (ChatPanel) so widgets don't have to thread
 * session/experiment through props.
 */
export interface AnalyticsContextValue {
  sessionId: string;
  visit_type?: string;
  site?: "hims" | "hers";
  environment?: AnalyticsEnvironment;
  experiment?: ExperimentContext;
  /** Resolved experience variant id. Populated in ChatPanel from
   *  `client.getExperience()` once the experience resolves. Required by the
   *  Wave 0.3 `latency:time_to_first_chip` emission site — Zod schema on
   *  the server rejects payloads without a non-empty variantId. */
  variantId?: string;
}

const AnalyticsContext = createContext<AnalyticsContextValue | null>(null);

export function AnalyticsProvider({
  children,
  value,
}: {
  children: ReactNode;
  value: AnalyticsContextValue;
}) {
  const memo = useMemo(
    () => value,
    [
      value.sessionId,
      value.visit_type,
      value.site,
      value.environment,
      value.experiment?.experiment_key,
      value.experiment?.variant,
      value.variantId,
    ],
  );
  return <AnalyticsContext.Provider value={memo}>{children}</AnalyticsContext.Provider>;
}

/** Returns null if no provider is mounted — callers decide whether to emit. */
export function useAnalyticsContext(): AnalyticsContextValue | null {
  return useContext(AnalyticsContext);
}
